import Calendar16 from '@carbon/icons-react/es/calendar/16';
import groupBy from 'lodash/groupBy';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Company } from 'api/models';
import { Project as ProjectT } from 'api/project';
import { CsatRating } from 'api/reports/csat';
import { Avatar } from 'components/atoms/avatar';
import { MilestoneCompleted } from 'components/atoms/icons';
import { Rate as RateAtom } from 'components/atoms/rate';
import { Template } from 'data/templates/api';
import { unreachable } from 'utils/unreachable';
import { GroupBy } from '../group-by';

enum ResponseDate {
  ThisWeek = 'this_week',
  LastWeek = 'last_week',
  ThisMonth = 'this_month',
  LastMonth = 'last_month',
  Older = 'older',
}

export interface GroupedCsatResult {
  key: string;
  headerData: Partial<{
    title: React.ReactNode | string;
    icon: React.ReactNode;
  }>;
  result: CsatRating[];
}

interface Dictionary<T> {
  [k: string]: T;
}

type Projects = Dictionary<ProjectT>;

type Data = CsatRating[];

const Rate = styled(RateAtom)`
  padding-left: var(--spacing-04);
`;

const determineResponseDateGroup = (value: number) => {
  const date = moment(value);
  const now = moment();

  if (date.isSame(now, 'week')) {
    return ResponseDate.ThisWeek;
  }
  if (date.isSame(now.subtract(1, 'week'), 'week')) {
    return ResponseDate.LastWeek;
  }
  if (date.isSame(now, 'month')) {
    return ResponseDate.ThisMonth;
  }
  if (date.isSame(now.subtract(1, 'month'), 'month')) {
    return ResponseDate.LastMonth;
  }

  return ResponseDate.Older;
};

const groupByProject = (data: Data, projects: Projects): Entry[] => {
  const grouped = groupBy(data, 'project.projectId');

  return Object.entries(grouped)
    .sort((a: Entry, b: Entry) => {
      const projectA = projects[a[0]];
      const projectB = projects[b[0]];

      if (projectA.startDate < projectB.startDate) {
        return -1;
      }
      if (projectA.startDate > projectB.startDate) {
        return 1;
      }

      return 0;
    })
    .reverse();
};

type Entry = [string, CsatRating[]];

const groupByRating = (data: Data): Entry[] => {
  const grouped = groupBy(data, (d) => parseInt(String(d.rating || 0), 10));

  return Object.entries(grouped).sort(
    (a: Entry, b: Entry) => Number(b[0]) - Number(a[0]),
  );
};

const groupByCustomer = (data: Data, companies: Dictionary<Company>) => {
  const grouped = groupBy(data, (d) => d.actionBy.company.companyId);

  return Object.entries(grouped).sort((a: Entry, b: Entry) => {
    const companyA = companies[a[0]];
    const companyB = companies[b[0]];

    if (companyA.companyName < companyB.companyName) {
      return -1;
    }
    if (companyA.companyName > companyB.companyName) {
      return 1;
    }

    return 0;
  });
};

export const getGroupedData = (
  groupByKey: GroupBy,
  data: CsatRating[] = [],
): GroupedCsatResult[] => {
  const projects: Projects = data
    .map((d) => d.project)
    .reduce((obj, next) => {
      return {
        ...obj,
        [next.projectId]: next,
      };
    }, {});

  const companies: Dictionary<Company> = data
    .map((d) => d.actionBy)
    .reduce((obj, next) => {
      return {
        ...obj,
        [next.company.companyId]: next.company,
      };
    }, {});

  const milestones: Dictionary<Template> = data
    .map((d) => d.taskTemplate)
    .reduce((obj, next) => {
      if (next && next.templateId) {
        return {
          ...obj,
          [next.templateId]: next,
        };
      }

      return {
        ...obj,
      };
    }, {});

  switch (groupByKey) {
    case GroupBy.Project:
      return groupByProject(data, projects).map(([projectId, result]) => {
        const project = projects[projectId];

        return {
          key: projectId,
          headerData: {
            title: project.projectName,
            icon: (
              <Avatar
                src={project.customer.companyLogoUrl}
                name={project.projectName}
              />
            ),
          },
          result,
        };
      });
    case GroupBy.ResponseDate:
      return Object.entries(
        groupBy(data, ({ actionAt }) => determineResponseDateGroup(actionAt)),
      ).map(([key, result]) => {
        return {
          key,
          result,
          headerData: {
            title: <FormattedMessage id={key} />,
            icon: <Calendar16 />,
          },
        };
      });
    case GroupBy.Rating:
      return groupByRating(data).map(([key, result]) => {
        return {
          key,
          result,
          headerData: {
            title: <Rate value={Number(key)} disabled />,
            icon: undefined,
          },
        };
      });
    case GroupBy.Milestone:
      return Object.entries(
        groupBy(data, (d) => d.taskTemplate?.templateId),
      ).map(([key, result]) => {
        return {
          key,
          result,
          headerData: {
            title: milestones[key]?.templateName || (
              <FormattedMessage id="ad_hoc_milestone" />
            ),
            icon: <MilestoneCompleted />,
          },
        };
      });

    case GroupBy.Customer:
      return groupByCustomer(data, companies).map(([key, result]) => {
        const company = companies[key];

        return {
          key,
          result,
          headerData: {
            title: company.companyName,
            icon: (
              <Avatar src={company.companyLogoUrl} name={company.companyName} />
            ),
          },
        };
      });
    default:
      return unreachable(groupByKey);
  }
};
